import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform } from 'react-native';
import { Button } from 'react-native-elements';
import * as Speech from 'expo-speech';
import { soundService } from '../../services/soundService';
import { colors, spacing, typography, globalStyles } from '../../styles/globalStyles';
import wordsData from '../../data/words.json';
import { testWordComparison } from '../../utils/wordComparison';

declare global {
  interface Window {
    webkitSpeechRecognition: any;
  }
}

type GameState = 'setup' | 'gameplay' | 'results';

export default function EchoChamberGame() {
  // Game settings
  const [gameState, setGameState] = useState<GameState>('setup');
  const [difficulty, setDifficulty] = useState('grade1');
  const [speedMode, setSpeedMode] = useState(false);
  
  // Game state
  const [sequence, setSequence] = useState<string[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  const [comparisonResult, setComparisonResult] = useState<{
    heard: string;
    expected: string[];
    errorIndex: number;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [dailyHighScore, setDailyHighScore] = useState(0);

  const recognitionRef = useRef<any>(null);
  const isProcessingRef = useRef(false);

  const handleGameOver = useCallback(() => {
    setGameOver(true);
    setGameState('results');
    setIsListening(false);

    // Stop recognition when the game is over
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }

    // Update high scores
    if (sequence.length > highScore) {
      setHighScore(sequence.length);
    }
    
    // Update daily high score
    if (sequence.length > dailyHighScore) {
      setDailyHighScore(sequence.length);
    }
  }, [sequence.length, highScore, dailyHighScore]);

  const addNewWord = useCallback(() => {
    const words = wordsData[difficulty]?.beginner;
    
    if (!words || words.length === 0) {
      Alert.alert('Error', 'No words available for the selected difficulty level.');
      return;
    }
    
    // Get a new word that's not already in the sequence
    let newWord;
    do {
      newWord = words[Math.floor(Math.random() * words.length)];
    } while (sequence.includes(newWord));
    
    setSequence(prev => [...prev, newWord]);
    setCurrentWordIndex(prev => prev + 1);
    
    // Show the new word briefly
    Speech.speak(newWord, {
      language: 'en',
      pitch: 1,
      rate: speedMode ? 1 : 0.75,
    });
  }, [difficulty, speedMode, sequence]);

  const checkSequence = useCallback((transcript: string) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      const spokenWords = transcript.toLowerCase().trim().split(/\s+/);
      console.log('Spoken words:', spokenWords);
      const expectedSequence = sequence.map(word => word.toLowerCase());
      console.log('Expected sequence:', expectedSequence);

      let errorIndex = -1;
      for (let i = 0; i < expectedSequence.length; i++) {
        if (i >= spokenWords.length || spokenWords[i] !== expectedSequence[i]) {
          errorIndex = i;
          break;
        }
      }

      console.log('Comparison result:', { heard: transcript, expected: expectedSequence, errorIndex });

      setComparisonResult({
        heard: transcript,
        expected: expectedSequence,
        errorIndex: errorIndex
      });

      if (errorIndex === -1) {
        console.log('Sequence correct, adding new word...');
        soundService.playSound('correct');
        setTimeout(() => {
          // Display the full sequence briefly
          setRecognizedText(spokenWords.join(' '));
          setTimeout(() => {
            addNewWord();
            setComparisonResult(null);
          }, 2000);
        }, 1500);
      } else {
        console.log('Sequence incorrect, game over.');
        soundService.playSound('incorrect');
        handleGameOver();
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [sequence, addNewWord, handleGameOver]);

  // Setup Speech Recognition
  const setupRecognition = useCallback(() => {
    if (Platform.OS === 'web' && window.webkitSpeechRecognition) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        setComparisonResult(null);
      };

      recognition.onresult = (event: any) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }

        // If we found any final transcript, process it
        if (finalTranscript) {
          console.log('Final recognized transcript:', finalTranscript);
          setRecognizedText(finalTranscript);
          checkSequence(finalTranscript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        if (!gameOver) {
          recognition.start();
        }
      };

      return recognition;
    }
    return null;
  }, [checkSequence, gameOver]);

  const startListening = useCallback(() => {
    console.log('Start listening button pressed');
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    recognitionRef.current = setupRecognition();
    
    if (recognitionRef.current) {
      recognitionRef.current.start();
    } else {
      Alert.alert('Not Supported', 'Speech recognition is not supported on this platform.');
    }
  }, [setupRecognition]);

  const startGame = useCallback(() => {
    console.log('Game started');
    setGameState('gameplay');
    setSequence([]);
    setCurrentWordIndex(0);
    setGameOver(false);
    setIsListening(false);
    setRecognizedText('');
    
    // Add first word after a short delay
    setTimeout(() => {
      addNewWord();
    }, 500);
  }, [addNewWord]);

  const renderSetup = () => (
    <ScrollView contentContainerStyle={[globalStyles.contentContainer]}>
      <Text style={[typography.title, styles.title]}>Echo Chamber</Text>
      
      <View style={[globalStyles.card, styles.settingsCard]}>
        <Text style={typography.subtitle}>Game Settings</Text>
        
        <Text style={[typography.body, styles.settingLabel]}>Grade Level:</Text>
        <View style={styles.gradeSelector}>
          {Object.keys(wordsData).map((grade) => (
            <TouchableOpacity
              key={grade}
              onPress={() => setDifficulty(grade)}
              style={[
                styles.gradeButton,
                difficulty === grade && styles.selectedGrade
              ]}
            >
              <Text style={[
                styles.gradeButtonText,
                difficulty === grade && styles.selectedGradeText
              ]}>
                {grade.replace('grade', 'Grade ')}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.speedModeContainer}>
          <Text style={typography.body}>Speed Mode:</Text>
          <TouchableOpacity
            onPress={() => setSpeedMode(!speedMode)}
            style={[
              styles.speedButton,
              speedMode && styles.speedButtonActive
            ]}
          >
            <Text style={[
              styles.speedButtonText,
              speedMode && styles.speedButtonTextActive
            ]}>
              {speedMode ? 'ON' : 'OFF'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={[globalStyles.card, styles.highScoresCard]}>
        <Text style={typography.subtitle}>High Scores</Text>
        <Text style={typography.body}>Personal Best: {highScore} words</Text>
        <Text style={typography.body}>Today's Best: {dailyHighScore} words</Text>
      </View>

      <TouchableOpacity
        onPress={startGame}
        style={[styles.startButton]}
      >
        <Text style={styles.startButtonText}>Start Game</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  const renderGameplay = () => (
    <View style={[globalStyles.container, styles.gameplayContainer]}>
      <View style={[globalStyles.card, styles.statsCard]}>
        <View style={globalStyles.statsContainer}>
          <View style={globalStyles.statBox}>
            <Text style={globalStyles.statLabel}>Sequence Length</Text>
            <Text style={globalStyles.statValue}>{sequence.length}</Text>
          </View>
        </View>
      </View>

      <View style={styles.gameStatus}>
        <View style={styles.wordContainer}>
          <Text style={typography.subtitle}>
            {isListening ? 'Recite the entire sequence:' : 'New word to remember:'}
          </Text>
          <Text style={[globalStyles.wordDisplay, styles.currentWord]}>
            {sequence[sequence.length - 1] || ''}
          </Text>
          
          {comparisonResult && (
            <View style={styles.comparisonContainer}>
              <Text style={typography.body}>System heard:</Text>
              <Text style={styles.heardText}>{comparisonResult.heard}</Text>
              
              {comparisonResult.errorIndex !== -1 && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>
                    Error at word {comparisonResult.errorIndex + 1}:
                  </Text>
                  <Text style={styles.expectedText}>
                    Expected: "{comparisonResult.expected[comparisonResult.errorIndex]}"
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      <TouchableOpacity
        onPress={startListening}
        disabled={isListening}
        style={[
          styles.listenButton,
          isListening && styles.listenButtonDisabled
        ]}
      >
        <Text style={styles.listenButtonText}>
          {isListening ? 'Listening...' : 'Start Speaking'}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderResults = () => (
    <ScrollView contentContainerStyle={[globalStyles.contentContainer]}>
      <Text style={[typography.title, styles.title]}>Game Over!</Text>
      
      <View style={[globalStyles.card, styles.resultsCard]}>
        <Text style={typography.subtitle}>Final Score: {sequence.length} words</Text>
        <Text style={[typography.body, styles.resultText]}>
          You memorized {sequence.length} words in sequence!
        </Text>
        
        <View style={styles.wordListContainer}>
          <Text style={typography.body}>Your sequence:</Text>
          {sequence.map((word, index) => (
            <Text key={index} style={styles.wordListItem}>
              {index + 1}. {word} {comparisonResult?.errorIndex === index ? <Text style={styles.errorText}>({comparisonResult.heard.split(' ')[index]})</Text> : ''}
            </Text>
          ))}
        </View>
        
        {sequence.length >= highScore && (
          <Text style={styles.newHighScore}>New Personal Best! 🎉</Text>
        )}
      </View>

      <Button
        title="Play Again"
        onPress={() => {
          setGameState('setup');
          setSequence([]);
          setCurrentWordIndex(0);
          setGameOver(false);
          setIsListening(false);
          setRecognizedText('');
          setComparisonResult(null);
        }}
        buttonStyle={[globalStyles.buttonPrimary, styles.playAgainButton]}
        titleStyle={globalStyles.buttonTextPrimary}
      />
    </ScrollView>
  );

  return (
    <View style={globalStyles.container}>
      {gameState === 'setup' && renderSetup()}
      {gameState === 'gameplay' && renderGameplay()}
      {gameState === 'results' && renderResults()}
    </View>
  );
}

const styles = StyleSheet.create({
  title: {
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  settingsCard: {
    marginBottom: spacing.lg,
  },
  settingLabel: {
    marginBottom: spacing.sm,
  },
  gradeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  gradeButton: {
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
    marginRight: spacing.sm,
    marginBottom: spacing.sm,
  },
  selectedGrade: {
    backgroundColor: colors.primary,
  },
  gradeButtonText: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  selectedGradeText: {
    color: colors.surface,
  },
  speedModeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.md,
  },
  speedButton: {
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  speedButtonActive: {
    backgroundColor: colors.primary,
  },
  speedButtonText: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  speedButtonTextActive: {
    color: colors.surface,
  },
  highScoresCard: {
    marginBottom: spacing.lg,
  },
  startButton: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    borderRadius: 25,
    marginTop: spacing.xl,
    alignItems: 'center',
  },
  startButtonText: {
    color: colors.surface,
    fontSize: 18,
    fontWeight: '700',
  },
  gameplayContainer: {
    padding: spacing.md,
  },
  statsCard: {
    marginBottom: spacing.lg,
  },
  gameStatus: {
    alignItems: 'center',
    marginVertical: spacing.xl,
  },
  wordContainer: {
    alignItems: 'center',
    width: '100%',
    padding: spacing.lg,
    backgroundColor: colors.surface,
    borderRadius: 10,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      },
    }),
  },
  currentWord: {
    marginVertical: spacing.lg,
  },
  comparisonContainer: {
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.background,
    borderRadius: 8,
    width: '100%',
  },
  heardText: {
    color: colors.text.secondary,
    fontSize: 16,
    marginTop: spacing.xs,
  },
  errorContainer: {
    marginTop: spacing.md,
    padding: spacing.sm,
    backgroundColor: '#ffebee',
    borderRadius: 4,
  },
  errorText: {
    color: colors.error,
    fontWeight: '600',
  },
  expectedText: {
    color: colors.text.primary,
    marginTop: spacing.xs,
  },
  listenButton: {
    backgroundColor: colors.primary,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: 25,
    marginTop: spacing.xl,
    alignItems: 'center',
  },
  listenButtonDisabled: {
    backgroundColor: colors.text.disabled,
  },
  listenButtonText: {
    color: colors.surface,
    fontSize: 18,
    fontWeight: '700',
  },
  resultsCard: {
    alignItems: 'center',
    padding: spacing.xl,
  },
  resultText: {
    marginTop: spacing.md,
    textAlign: 'center',
  },
  wordListContainer: {
    width: '100%',
    marginTop: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.background,
    borderRadius: 8,
  },
  wordListItem: {
    marginTop: spacing.sm,
    fontSize: 16,
    color: colors.text.primary,
  },
  newHighScore: {
    marginTop: spacing.lg,
    color: colors.primary,
    fontSize: 20,
    fontWeight: '700',
  },
  playAgainButton: {
    marginTop: spacing.xl,
  },
}); 